import { c as _c } from "react/compiler-runtime";
import { ArrowUp, ArrowUpRight, Icons, MoreVertical } from '@/components/ui/icons/Icons';
import { Badge } from '@/components/ui/Badge';
import { useI18n } from '@/context/I18nProvider';
import { usePendingReviewsCount } from '@/hooks/usePendingReviewsCount';
import { cn } from '@/lib/utils';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import { createElement, useEffect, useState } from 'react';
type NavItem = {
  href: string;
  labelKey: string;
  icon: keyof typeof Icons;
  openInNewTab?: boolean;
};

// ─── Settings mode ──────────────────────────────────────────────
const settingsNavItems: NavItem[] = [{
  href: '/settings/account/profile',
  labelKey: 'settings.profile',
  icon: 'User3'
}, {
  href: '/settings/account/password',
  labelKey: 'settings.password',
  icon: 'Key3'
}, {
  href: '/settings/workspace/members',
  labelKey: 'settings.members',
  icon: 'TripleUser'
}, {
  href: '/settings/workspace/billing',
  labelKey: 'billing.title',
  icon: 'Billing'
}];
const settingsMoreItems: NavItem[] = [{
  href: '/settings/workspace/conversations',
  labelKey: 'settings.conversations',
  icon: 'DoubleConversation'
}, {
  href: '/contact-support',
  labelKey: 'navigation.contactSupport',
  icon: 'Calendar'
},
// {
//   href: 'https://orsayai.canny.io/feature-requests',
//   labelKey: 'navigation.requestFeature',
//   icon: 'MagicWand',
//   openInNewTab: true,
// },
{
  href: 'https://docs.orsay.ai/',
  labelKey: 'navigation.documentation',
  icon: 'BookOpen',
  openInNewTab: true
}];

// Routes that trigger settings mode (same logic as AppSidebar)
const allSettingsRoutes = ['/settings/account/profile', '/settings/account/password', '/settings/workspace/members', '/settings/workspace/billing', '/settings/workspace/conversations', '/contact-support'];
const MobileBottomNav = () => {
  const $ = _c(59);
  const currentPath = usePathname();
  const router = useRouter();
  const {
    t
  } = useI18n();
  const [showMore, setShowMore] = useState(false);
  const pendingReviewsCount = usePendingReviewsCount();
  let t0;
  if ($[0] !== router) {
    t0 = allSettingsRoutes.some(route => router.route.startsWith(route));
    $[0] = router;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isSettingsMode = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setShowMore(false);
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== currentPath) {
    t2 = [currentPath];
    $[3] = currentPath;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== currentPath) {
    t3 = item => currentPath.startsWith(item.href);
    $[5] = currentPath;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const isActive = t3;
  let isMoreActive;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[7] !== isActive || $[8] !== isSettingsMode || $[9] !== pendingReviewsCount || $[10] !== router || $[11] !== showMore || $[12] !== t) {
    const mainBarItems = [{
      href: "/conversations",
      labelKey: "navigation.conversations",
      icon: "DoubleConversation"
    }, {
      href: "/analytics",
      labelKey: "navigation.analytics",
      icon: "AnalyticsSidebar"
    }];
    const mainMoreItemsList = [{
      href: "/agents",
      labelKey: "navigation.agents",
      icon: "AgentSidebar" as keyof typeof Icons
    }, {
      href: "/sequences",
      labelKey: "navigation.sequences",
      icon: "SequencesSidebar"
    }, {
      href: "/campaigns",
      labelKey: "navigation.campaigns",
      icon: "CampaignSidebar"
    }, {
      href: "/integrations",
      labelKey: "navigation.integrations",
      icon: "FineTuning"
    }, {
      href: "/conversation-improvements",
      labelKey: "navigation.conversationImprovements",
      icon: "MagicWand"
    }, {
      href: "/referral",
      labelKey: "referral.title",
      icon: "Heart"
    }, {
      href: "https://docs.orsay.ai/",
      labelKey: "navigation.documentation",
      icon: "BookOpen",
      openInNewTab: true
    }];
    const navItems = isSettingsMode ? settingsNavItems : mainBarItems;
    const moreItems = isSettingsMode ? settingsMoreItems : mainMoreItemsList;
    let t8;
    if ($[18] !== isActive) {
      t8 = item_0 => !item_0.openInNewTab && isActive(item_0);
      $[18] = isActive;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    isMoreActive = moreItems.some(t8);
    let t9;
    if ($[20] !== isActive || $[21] !== pendingReviewsCount || $[22] !== t) {
      t9 = item_1 => {
        const active = !item_1.openInNewTab && isActive(item_1);
        const isExternal = item_1.openInNewTab;
        return <Link key={item_1.href} href={item_1.href} onClick={() => setShowMore(false)} target={isExternal ? "_blank" : undefined} rel={isExternal ? "noopener noreferrer" : undefined} className={cn("flex items-center gap-3 px-3 py-2.5 rounded-lg", active ? "text-primary bg-[#FFF7ED]" : "text-[#4B5563]")}>{createElement(Icons[item_1.icon], {
            className: cn("size-5 shrink-0", active ? "text-primary" : "text-[#4B5563]")
          })}<span className="text-sm font-medium">{t(item_1.labelKey as any)}</span>{item_1.href === "/conversation-improvements" && pendingReviewsCount > 0 && <Badge variant="destructive" className="ml-auto h-5 min-w-5 px-1.5 text-[10px] font-semibold">{pendingReviewsCount}</Badge>}{isExternal && <ArrowUpRight className="ml-auto size-4 text-[#4B5563]" />}</Link>;
      };
      $[20] = isActive;
      $[21] = pendingReviewsCount;
      $[22] = t;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    const renderMoreMenuItem = t9;
    let t10;
    if ($[24] !== isActive || $[25] !== t) {
      t10 = item_2 => {
        const active_0 = isActive(item_2);
        return <Link key={item_2.href} href={item_2.href} className={cn("flex flex-col items-center justify-center gap-0.5 px-1 py-1 rounded-lg min-w-0 flex-1", active_0 ? "text-[#1E89FF]" : "text-[#4B5563]")}>{createElement(Icons[item_2.icon], {
            className: cn("size-5 shrink-0", active_0 ? "text-[#1E89FF]" : "text-[#4B5563]")
          })}<span className="text-[10px] font-medium leading-tight truncate w-full text-center">{t(item_2.labelKey as any)}</span></Link>;
      };
      $[24] = isActive;
      $[25] = t;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    const renderNavButton = t10;
    t7 = showMore && <div className="fixed inset-0 z-40" onClick={() => setShowMore(false)}><div className="absolute bottom-[52px] left-0 right-0 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-2 py-2" onClick={_temp}>{moreItems.map(renderMoreMenuItem)}</div></div>;
    t4 = "flex items-center justify-around bg-white border-t border-gray-200 px-1 py-1.5";
    if ($[27] !== isSettingsMode || $[28] !== router || $[29] !== t) {
      t5 = isSettingsMode && <button onClick={() => router.push("/conversations")} className="flex flex-col items-center justify-center gap-0.5 px-1 py-1 rounded-lg min-w-0 flex-1 text-[#4B5563]"><ArrowUp className="size-5 shrink-0 text-[#4B5563]" /><span className="text-[10px] font-medium leading-tight truncate w-full text-center">{t("common.back")}</span></button>;
      $[27] = isSettingsMode;
      $[28] = router;
      $[29] = t;
      $[30] = t5;
    } else {
      t5 = $[30];
    }
    t6 = navItems.map(renderNavButton);
    $[7] = isActive;
    $[8] = isSettingsMode;
    $[9] = pendingReviewsCount;
    $[10] = router;
    $[11] = showMore;
    $[12] = t;
    $[13] = isMoreActive;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    isMoreActive = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
  }
  let t8;
  if ($[31] !== showMore) {
    t8 = () => setShowMore(!showMore);
    $[31] = showMore;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const t9 = isMoreActive || showMore ? "text-[#1E89FF]" : "text-[#4B5563]";
  let t10;
  if ($[33] !== t9) {
    t10 = cn("relative flex flex-col items-center justify-center gap-0.5 px-1 py-1 rounded-lg min-w-0 flex-1", t9);
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== pendingReviewsCount) {
    t11 = pendingReviewsCount > 0 && <span className="absolute top-0.5 right-2 size-2 rounded-full bg-red-500" />;
    $[35] = pendingReviewsCount;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  const t12 = isMoreActive || showMore ? "text-[#1E89FF]" : "text-[#4B5563]";
  let t13;
  if ($[37] !== t12) {
    t13 = cn("size-5 shrink-0", t12);
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t13) {
    t14 = <MoreVertical className={t13} />;
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t) {
    t15 = t("ui.more");
    $[41] = t;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t15) {
    t16 = <span className="text-[10px] font-medium leading-tight truncate w-full text-center">{t15}</span>;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t10 || $[46] !== t11 || $[47] !== t14 || $[48] !== t16 || $[49] !== t8) {
    t17 = <button onClick={t8} className={t10}>{t11}{t14}{t16}</button>;
    $[45] = t10;
    $[46] = t11;
    $[47] = t14;
    $[48] = t16;
    $[49] = t8;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== t17 || $[52] !== t4 || $[53] !== t5 || $[54] !== t6) {
    t18 = <nav className={t4}>{t5}{t6}{t17}</nav>;
    $[51] = t17;
    $[52] = t4;
    $[53] = t5;
    $[54] = t6;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== t18 || $[57] !== t7) {
    t19 = <>{t7}{t18}</>;
    $[56] = t18;
    $[57] = t7;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  return t19;
};
export { MobileBottomNav };
function _temp(e) {
  return e.stopPropagation();
}